import { z } from 'zod';
import type { GeneratedItem, ItemId, RecipeRecord } from '@/types/domain';

const historyEntrySchema = z.object({
  id: z.string(),
  at: z.number(),
  key: z.string(),
  resultId: z.string().nullable(),
  isVanilla: z.boolean(),
  label: z.string(),
});

const favoriteSchema = z.object({ id: z.string(), at: z.number() });

export const saveBundleSchema = z.object({
  app: z.string(),
  version: z.number(),
  exportedAt: z.number().optional(),
  items: z.array(z.any()),
  recipes: z.array(z.any()),
  history: z.array(z.any()).default([]),
  favorites: z.array(z.any()).default([]),
  settings: z.record(z.any()).default({}),
});

export interface SaveBundle {
  app: string;
  version: number;
  exportedAt?: number;
  items: GeneratedItem[];
  recipes: RecipeRecord[];
  history: Array<{ id: string; at: number; key: string; resultId: ItemId | null; isVanilla: boolean; label: string }>;
  favorites: Array<{ id: ItemId; at: number }>;
  settings: Record<string, unknown>;
}

/** Validates and lightly coerces an imported save object. */
export function normalizeImportedSave(data: unknown): SaveBundle | null {
  if (!data || typeof data !== 'object') return null;
  const parsed = saveBundleSchema.safeParse(data);
  if (!parsed.success) return null;
  const d = parsed.data;
  // Best-effort structural pass-through; deeply untrusted records are re-validated
  // by the repositories when they are used (Zod-parsed at read time where useful).
  return {
    app: d.app,
    version: d.version,
    exportedAt: d.exportedAt,
    items: d.items as GeneratedItem[],
    recipes: d.recipes as RecipeRecord[],
    history: d.history.filter((h): h is z.infer<typeof historyEntrySchema> => historyEntrySchema.safeParse(h).success),
    favorites: d.favorites.filter((f): f is z.infer<typeof favoriteSchema> => favoriteSchema.safeParse(f).success),
    settings: d.settings,
  };
}

export function isSaveFile(data: unknown): boolean {
  return normalizeImportedSave(data) !== null;
}
